import React from "react";
import { useTranslation } from "react-i18next";
import APIRequest, { APIError } from "system/APIRequest";
import { permissionTest } from "system/InfegyUser";

interface WorkrampRedirectResponse {
  redirect: string | null;
}

export interface UseStarscapeAcademyResult {
  openAcademy: () => Promise<boolean>;
  errorMessage: string;
  isLoading: boolean;
}

// Custom hook for fetching and opening the Starscape Academy redirect URL on click.
// TODO: Remove "infegy_admin" permission test before Production launch
export function useStarscapeAcademy(): UseStarscapeAcademyResult {
  const { i18n } = useTranslation();

  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const openAcademy = React.useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setErrorMessage("");

    // Open a blank tab synchronously so browsers allow it from this click event.
    // Do not pass "noopener" here, or some browsers return null and we lose the handle.
    const academyTab = window.open("", "_blank");
    if (academyTab) {
      academyTab.opener = null;
    }

    try {
      // Bypass APIRequest/FetchQueue cache so each click gets a freshly generated redirect URL.
      const response = await APIRequest("workramp_redirect", undefined, { method: "GET" }, true) as WorkrampRedirectResponse;
      const url = (response && typeof response.redirect === "string" && response.redirect.length > 0) ? response.redirect : null;

      if (!url) {
        throw new Error(i18n.t("nav.academy-unavailable"));
      }

      if (academyTab) {
        academyTab.location.href = url;
      } else {
        // Fallback when popup was blocked or no tab handle was returned.
        window.open(url, "_blank", "noopener");
      }

      return true;
    } catch (error) {
      if (academyTab) {
        academyTab.close();
      }

      setErrorMessage(
        (error instanceof APIError && error.server_message)
          ? error.server_message
          : (error instanceof Error && error.message)
            ? error.message
            : i18n.t("nav.academy-unavailable")
      );

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [i18n]);

  return { openAcademy, errorMessage, isLoading };
}
